import React, { useState, createContext, useContext } from 'react';
import { Role, type User } from '../auth/capabilities';
interface AuthContextType {
  role: 'agilebrain-admin' | 'client-admin';
  setRole: (role: 'agilebrain-admin' | 'client-admin') => void;
  isAdmin: boolean;
  users: User[];
  addUser: (user: User) => void;
  updateUser: (userId: string, updates: Partial<User>) => void;
  logout: () => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [role, setRole] = useState<'agilebrain-admin' | 'client-admin'>('agilebrain-admin');
  // Mock users for demonstration
  const [users, setUsers] = useState<User[]>([{
    id: 'user-1',
    name: 'Dr. Sarah Mitchell',
    email: 'sarah.mitchell@agilebrain.com',
    role: Role.AB_SUPER_ADMIN,
    scope: {
      allowed_org_ids: [],
      allowed_suborg_ids: [],
      allowed_group_ids: [],
      allowed_participant_ids: []
    },
    lastActive: new Date(Date.now() - 1000 * 60 * 15).toISOString()
  }, {
    id: 'user-2',
    name: 'Dr. James Chen',
    email: 'james.chen@agilebrain.com',
    role: Role.AB_SCIENTIST,
    scope: {
      allowed_org_ids: ['KS-ORG-KMG-001', 'KS-ORG-MVCC-001'],
      allowed_suborg_ids: [],
      allowed_group_ids: [],
      allowed_participant_ids: []
    },
    lastActive: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString()
  }, {
    id: 'user-3',
    name: 'Maria Rodriguez',
    email: 'maria.rodriguez@agilebrain.com',
    role: Role.AB_CSM,
    scope: {
      allowed_org_ids: ['KS-ORG-KMG-001'],
      allowed_suborg_ids: [],
      allowed_group_ids: [],
      allowed_participant_ids: []
    },
    lastActive: new Date(Date.now() - 1000 * 60 * 45).toISOString()
  }, {
    id: 'user-4',
    name: 'Alex Thompson',
    email: 'alex.thompson@kyivmentalguard.org',
    role: Role.ORG_SUPER_ADMIN,
    scope: {
      allowed_org_ids: ['KS-ORG-KMG-001'],
      allowed_suborg_ids: [],
      allowed_group_ids: [],
      allowed_participant_ids: []
    },
    lastActive: new Date(Date.now() - 1000 * 60 * 30).toISOString()
  }, {
    id: 'user-5',
    name: 'Emily Davis',
    email: 'emily.davis@kyivmentalguard.org',
    role: Role.ORG_ADMIN,
    scope: {
      allowed_org_ids: ['KS-ORG-KMG-001'],
      allowed_suborg_ids: ['school-42-kyiv'],
      allowed_group_ids: [],
      allowed_participant_ids: []
    },
    lastActive: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString()
  }]);
  const addUser = (user: User) => {
    setUsers([...users, user]);
  };
  const updateUser = (userId: string, updates: Partial<User>) => {
    setUsers(users.map(user => user.id === userId ? {
      ...user,
      ...updates
    } : user));
  };
  const logout = () => {
    localStorage.removeItem('demo-authenticated');
    window.location.reload();
  };
  return <AuthContext.Provider value={{
    role,
    setRole,
    isAdmin: role === 'agilebrain-admin',
    users,
    addUser,
    updateUser,
    logout
  }} data-id="element-584">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}